import React, { memo, useState, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Handle, Position, useReactFlow } from '@xyflow/react';
import type { NodeProps } from '@xyflow/react';
import { useTenantView } from '../TenantViewContext';
import { getNodePulseLevel, isNodeWiredToLunaCore } from '../pulseUtils';
import KnowledgeExplorerModal from '../KnowledgeExplorerModal';
import {
  BookOpen,
  Database,
  Zap,
  FileText,
  CheckCircle2,
  UploadCloud,
  FileUp,
  Brain,
  Sparkles,
  RefreshCw,
  Trash2,
  Edit3,
  Eye,
  Tag,
  AlertCircle,
  Maximize2,
  Layers,
  HelpCircle,
  Package,
  Shield,
  Building2,
  Phone,
  ChevronRight,
  ArrowRight,
  Send,
  Globe,
  ExternalLink,
  Compass,
  Type,
  X,
  AlignLeft
} from 'lucide-react';

export interface KnowledgeNodeData {
  knowledgeType?: 'catalog' | 'pdf' | 'faq' | 'custom';
  contextSource?: string;
  autoSync?: boolean;
  lastTrainedAt?: string;
  lastTrainedFileName?: string;
  lastTrainedFileSize?: number;
  autoKeywords?: string;
  scrapedUrl?: string;
  [key: string]: unknown;
}

export const KnowledgeNode = memo(({ id, data, selected }: NodeProps) => {
  const { setNodes, getEdges, getNodes } = useReactFlow();
  const nodeData = (data || {}) as KnowledgeNodeData;

  const [isTraining, setIsTraining] = useState(false);
  const [trainingPhase, setTrainingPhase] = useState<'uploading' | 'synthesizing' | null>(null);
  const [trainingError, setTrainingError] = useState<string | null>(null);

  const sanitizeError = (err: any, fallback: string): string => {
    const raw = String(err?.message || fallback);
    return raw
      .replace(/models\/[a-zA-Z0-9._-]+/gi, 'Luna AI')
      .replace(/gemini-[a-zA-Z0-9._-]+/gi, 'Luna AI')
      .replace(/GoogleGenAI/gi, 'Luna AI')
      .replace(/Gemini AI/gi, 'Luna AI')
      .replace(/Gemini/gi, 'Luna AI')
      .trim();
  };
  const [trainingTab, setTrainingTab] = useState<'upload' | 'text' | 'scrape'>('upload');
  const [rawTextInput, setRawTextInput] = useState('');
  const [rawTextTitle, setRawTextTitle] = useState('');
  const [pendingRawText, setPendingRawText] = useState<{ text: string; title: string } | null>(null);
  const [targetUrlInput, setTargetUrlInput] = useState(nodeData.scrapedUrl || '');
  const [urlCrawlDepth, setUrlCrawlDepth] = useState<'single' | 'deep'>('single');
  const [pendingUrlScrape, setPendingUrlScrape] = useState<{ url: string; depth: string } | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [showIngestModal, setShowIngestModal] = useState<boolean>(false);
  const [selectedIngestMode, setSelectedIngestMode] = useState<'merge' | 'replace'>('merge');
  const [saveIndicator, setSaveIndicator] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Local draft state to prevent cursor jumping when typing in RAG instructions / contextSource
  const [localContextSource, setLocalContextSource] = useState<string>(() => nodeData.contextSource || '');
  const isContextTypingRef = useRef<boolean>(false);
  const contextTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const contextSyncDebounceTimerRef = useRef<any>(null);

  React.useEffect(() => {
    if (!isContextTypingRef.current && nodeData.contextSource !== undefined && nodeData.contextSource !== localContextSource) {
      setLocalContextSource(nodeData.contextSource);
    }
  }, [nodeData.contextSource]);

  const getAuthHeaders = (): Record<string, string> => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (isTenantView) {
      if (authToken) headers['Authorization'] = authToken.startsWith('Bearer ') ? authToken : `Bearer ${authToken}`;
      if (tenantApiKey) headers['x-tenant-api-key'] = tenantApiKey;
      const tenetToken = sessionStorage.getItem('tenetToken') || localStorage.getItem('tenetToken');
      if (!headers['Authorization'] && tenetToken) headers['Authorization'] = `Bearer ${tenetToken}`;
    } else {
      const token = sessionStorage.getItem('adminToken') || '';
      const godToken = sessionStorage.getItem('godModeToken') || '';
      const tenetToken = sessionStorage.getItem('tenetToken') || localStorage.getItem('tenetToken');
      if (token) headers['Authorization'] = `Bearer ${token}`;
      else if (tenetToken) headers['Authorization'] = `Bearer ${tenetToken}`;
      if (godToken) headers['X-God-Mode-Token'] = godToken;
    }
    return headers;
  };

  const persistNodeDataDirectly = async (updatedSource: string, extraData: Record<string, any> = {}) => {
    try {
      const headers = getAuthHeaders();
      const payload = {
        nodeId: id,
        data: {
          contextSource: updatedSource,
          lastTrainedAt: new Date().toISOString(),
          knowledgeType: nodeData.knowledgeType || 'pdf',
          ...extraData,
        },
      };

      setSaveIndicator('Saving...');
      const endpoint = isTenantView ? '/api/tenet/widgets/save-node-data' : '/api/admin/luna-flows/save-node-data';
      const res = await fetch(endpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        setSaveIndicator('✅ Saved to DB');
        setTimeout(() => setSaveIndicator(null), 3000);
      }
    } catch (err) {
      console.warn('[KnowledgeNode] Direct persist warning:', err);
    }
  };

  const handleChange = (field: keyof KnowledgeNodeData, value: any) => {
    setNodes((nds) =>
      nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, [field]: value } } : n))
    );
    if (field === 'contextSource') {
      persistNodeDataDirectly(value);
    }
  };

  const handleBulkUpdate = (updates: Partial<KnowledgeNodeData>) => {
    setNodes((nds) =>
      nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, ...updates } } : n))
    );
    if (updates.contextSource !== undefined) {
      persistNodeDataDirectly(updates.contextSource, updates);
    }
  };

  const handleContextSourceChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const nextVal = e.target.value;
    const cursor = e.target.selectionStart;
    setLocalContextSource(nextVal);

    if (contextSyncDebounceTimerRef.current) clearTimeout(contextSyncDebounceTimerRef.current);
    contextSyncDebounceTimerRef.current = setTimeout(() => {
      setNodes((nds) =>
        nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, contextSource: nextVal } } : n))
      );
      persistNodeDataDirectly(nextVal);
    }, 450);

    requestAnimationFrame(() => {
      if (contextTextareaRef.current && document.activeElement === contextTextareaRef.current) {
        contextTextareaRef.current.setSelectionRange(cursor, cursor);
      }
    });
  };

  const handleContextSourceFocus = () => {
    isContextTypingRef.current = true;
  };

  const handleContextSourceBlur = () => {
    isContextTypingRef.current = false;
    if (contextSyncDebounceTimerRef.current) clearTimeout(contextSyncDebounceTimerRef.current);
    setNodes((nds) =>
      nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, contextSource: localContextSource } } : n))
    );
    persistNodeDataDirectly(localContextSource);
  };

  const { isTenantView, authToken, tenantApiKey, pulseState, openPortalData, isCompactView, openInspector } = useTenantView();

  const pulseLevel = getNodePulseLevel(id, pulseState);
  const isNodeActive = pulseLevel === 'active';
  const isNodeTrail = pulseLevel === 'trail';
  const isWired = useMemo(() => isNodeWiredToLunaCore(id, getEdges(), getNodes()), [id, getEdges, getNodes]);

  const handleFileSelected = (file: File) => {
    if (!file) return;
    const hasExistingData = nodeData.contextSource && nodeData.contextSource.trim().length > 10;
    if (hasExistingData) {
      setPendingFile(file);
      setPendingRawText(null);
      setSelectedIngestMode('merge');
      setShowIngestModal(true);
    } else {
      executeFileUpload(file, 'merge');
    }
  };

  const handleRawTextSubmit = () => {
    if (!rawTextInput.trim()) return;
    const hasExistingData = nodeData.contextSource && nodeData.contextSource.trim().length > 10;
    const title = rawTextTitle.trim() || 'Pasted Notes';
    if (hasExistingData) {
      setPendingRawText({ text: rawTextInput.trim(), title });
      setPendingFile(null);
      setSelectedIngestMode('merge');
      setShowIngestModal(true);
    } else {
      executeRawTextTraining(rawTextInput.trim(), title, 'merge');
    }
  };

  const executeRawTextTraining = async (rawText: string, title: string, mode: 'merge' | 'replace') => {
    if (!rawText) return;
    setIsTraining(true);
    setTrainingPhase('synthesizing');
    setTrainingError(null);
    setShowIngestModal(false);

    try {
      const headers = getAuthHeaders();
      const payload = {
        rawText,
        sourceTitle: title || 'Pasted Notes',
        nodeId: id,
        mergeMode: mode,
        existingKnowledge: mode === 'replace' ? '' : (nodeData.contextSource || ''),
        knowledgeType: nodeData.knowledgeType || 'pdf',
      };

      const res = await fetch('/api/admin/luna-flows/train-raw-text', {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      });

      const result = await res.json();
      if (!res.ok || !result.success) {
        throw new Error(result.error || `Training failed with status ${res.status}`);
      }

      handleBulkUpdate({
        contextSource: result.structuredKnowledge,
        lastTrainedAt: new Date().toISOString(),
        lastTrainedFileName: title || 'Pasted Notes',
        lastTrainedFileSize: result.fileSizeBytes || rawText.length,
        autoKeywords: result.autoKeywords || nodeData.autoKeywords || '',
      });

      setRawTextInput('');
      setRawTextTitle('');
      setSaveIndicator('✅ Persisted to Database');
      setTimeout(() => setSaveIndicator(null), 3000);
    } catch (err: any) {
      console.error('[KnowledgeNode] Raw text training error:', err);
      setTrainingError(sanitizeError(err, 'Failed to train knowledge base from pasted text.'));
    } finally {
      setIsTraining(false);
      setTrainingPhase(null);
      setPendingRawText(null);
    }
  };

  const executeFileUpload = async (file: File, mode: 'merge' | 'replace') => {
    if (!file) return;
    setIsTraining(true);
    setTrainingPhase('uploading');
    setTrainingError(null);
    setShowIngestModal(false);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('nodeId', id);
      formData.append('mergeMode', mode);
      formData.append('existingKnowledge', mode === 'replace' ? '' : (nodeData.contextSource || ''));
      formData.append('knowledgeType', nodeData.knowledgeType || 'pdf');

      // ── Resolve Auth Headers ──
      const headers: Record<string, string> = {};
      if (isTenantView) {
        if (authToken) headers['Authorization'] = authToken.startsWith('Bearer ') ? authToken : `Bearer ${authToken}`;
        if (tenantApiKey) headers['x-tenant-api-key'] = tenantApiKey;
        const tenetToken = sessionStorage.getItem('tenetToken') || localStorage.getItem('tenetToken');
        if (!headers['Authorization'] && tenetToken) headers['Authorization'] = `Bearer ${tenetToken}`;
      } else {
        const token = sessionStorage.getItem('adminToken') || '';
        const godToken = sessionStorage.getItem('godModeToken') || '';
        const tenetToken = sessionStorage.getItem('tenetToken') || localStorage.getItem('tenetToken');
        if (token) headers['Authorization'] = `Bearer ${token}`;
        else if (tenetToken) headers['Authorization'] = `Bearer ${tenetToken}`;
        if (godToken) headers['X-God-Mode-Token'] = godToken;
      }

      setTrainingPhase('synthesizing');

      const res = await fetch('/api/admin/luna-flows/train-knowledge', {
        method: 'POST',
        headers,
        body: formData,
      });

      const result = await res.json();

      if (!res.ok || !result.success) {
        throw new Error(result.error || `Training failed with status ${res.status}`);
      }

      handleBulkUpdate({
        contextSource: result.structuredKnowledge,
        lastTrainedAt: new Date().toISOString(),
        lastTrainedFileName: file.name,
        lastTrainedFileSize: file.size,
        autoKeywords: result.autoKeywords || nodeData.autoKeywords || '',
      });

      setSaveIndicator('✅ Persisted to Database');
      setTimeout(() => setSaveIndicator(null), 3000);
    } catch (err: any) {
      console.error('[KnowledgeNode] Upload & training error:', err);
      setTrainingError(sanitizeError(err, 'Failed to train knowledge base from document.'));
    } finally {
      setIsTraining(false);
      setTrainingPhase(null);
      setPendingFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleUrlScrapeSubmit = () => {
    if (!targetUrlInput.trim()) return;
    const hasExistingData = nodeData.contextSource && nodeData.contextSource.trim().length > 10;
    const cleanUrl = targetUrlInput.trim();
    if (hasExistingData) {
      setPendingUrlScrape({ url: cleanUrl, depth: urlCrawlDepth });
      setPendingFile(null);
      setPendingRawText(null);
      setSelectedIngestMode('merge');
      setShowIngestModal(true);
    } else {
      executeUrlScrapeTraining(cleanUrl, urlCrawlDepth, 'merge');
    }
  };

  const executeUrlScrapeTraining = async (url: string, depth: string, mode: 'merge' | 'replace') => {
    if (!url) return;
    setIsTraining(true);
    setTrainingPhase('uploading');
    setTrainingError(null);
    setShowIngestModal(false);

    try {
      const headers = getAuthHeaders();
      const payload = {
        url,
        depth,
        nodeId: id,
        mergeMode: mode,
        existingKnowledge: mode === 'replace' ? '' : (nodeData.contextSource || ''),
        knowledgeType: nodeData.knowledgeType || 'pdf',
      };

      setTrainingPhase('synthesizing');

      const res = await fetch('/api/admin/luna-flows/train-website-url', {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      });

      const result = await res.json();
      if (!res.ok || !result.success) {
        throw new Error(result.error || `Website scraping failed with status ${res.status}`);
      }

      handleBulkUpdate({
        contextSource: result.structuredKnowledge,
        lastTrainedAt: new Date().toISOString(),
        lastTrainedFileName: `Web Scrape: ${url.replace(/^https?:\/\//, '')}`,
        lastTrainedFileSize: result.fileSizeBytes || 0,
        autoKeywords: result.autoKeywords || nodeData.autoKeywords || '',
        scrapedUrl: url,
      });

      setSaveIndicator('✅ Persisted to Database');
      setTimeout(() => setSaveIndicator(null), 3000);
    } catch (err: any) {
      console.error('[KnowledgeNode] Website URL scraping error:', err);
      setTrainingError(sanitizeError(err, 'Failed to scrape and train knowledge base from website.'));
    } finally {
      setIsTraining(false);
      setTrainingPhase(null);
      setPendingUrlScrape(null);
    }
  };

  const charCount = (nodeData.contextSource || '').length;
  const wordCount = (nodeData.contextSource || '').trim().split(/\s+/).filter(Boolean).length;

  // Detect available sections for quick pill badges
  const detectedCategories = useMemo(() => {
    const raw = (nodeData.contextSource || '').toLowerCase();
    const cats: { label: string; icon: React.ElementType; color: string }[] = [];
    if (/who we are|about us|company|mission|vision|story|purpose/i.test(raw)) {
      cats.push({ label: 'Identity', icon: Building2, color: 'text-purple-400 border-purple-500/30 bg-purple-500/10' });
    }
    if (/product|pricing|price|catalog|sku|package|plan/i.test(raw)) {
      cats.push({ label: 'Products', icon: Package, color: 'text-emerald-400 border-emerald-500/30 bg-emerald-500/10' });
    }
    if (/policy|operation|guideline|warranty|return|terms/i.test(raw)) {
      cats.push({ label: 'Policies', icon: Shield, color: 'text-cyan-400 border-cyan-500/30 bg-cyan-500/10' });
    }
    if (/faq|q:|question:|inquir/i.test(raw)) {
      cats.push({ label: 'FAQs', icon: HelpCircle, color: 'text-amber-400 border-amber-500/30 bg-amber-500/10' });
    }
    if (/contact|phone|email|location|branch|hotline/i.test(raw)) {
      cats.push({ label: 'Contact', icon: Phone, color: 'text-rose-400 border-rose-500/30 bg-rose-500/10' });
    }
    return cats;
  }, [nodeData.contextSource]);

  return (
    <>
      <div
        className={`rounded-2xl bg-gradient-to-b from-[#091515]/98 via-[#060D0D]/98 to-[#040808]/98 backdrop-blur-2xl border transition-all duration-300 w-[400px] shadow-[0_20px_50px_rgba(0,0,0,0.85),inset_0_1px_0_0_rgba(255,255,255,0.12)] relative select-none group ${
          isNodeActive
            ? 'border-cyan-300 ring-4 ring-cyan-400/60 shadow-[0_0_80px_rgba(34,211,238,0.8),inset_0_1px_0_0_rgba(255,255,255,0.2)] scale-[1.02] pulse-glow-active'
            : isNodeTrail
            ? 'border-emerald-400/70 ring-2 ring-emerald-400/30 shadow-[0_0_40px_rgba(52,211,153,0.4),inset_0_1px_0_0_rgba(255,255,255,0.15)]'
            : selected
            ? 'border-emerald-400 ring-2 ring-emerald-400/40 shadow-[0_0_30px_rgba(16,185,129,0.4),inset_0_1px_0_0_rgba(255,255,255,0.15)]'
            : 'border-emerald-500/35 hover:border-emerald-400/80 shadow-[0_20px_50px_rgba(0,0,0,0.85),0_0_20px_rgba(16,185,129,0.15),inset_0_1px_0_0_rgba(255,255,255,0.12)]'
        }`}
      >
        {/* ── Floating Cyber-HUD Action: View Live Data in Portal (Fades in on Hover) ── */}
        {openPortalData && (
          <div className="absolute -top-11 left-1/2 -translate-x-1/2 z-50 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-all duration-300 transform translate-y-1 group-hover:translate-y-0">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                openPortalData('knowledgeNode', id);
              }}
              className="nodrag flex items-center gap-2 px-4 py-1.5 rounded-full bg-gradient-to-r from-emerald-950 via-slate-900 to-teal-950 border-2 border-emerald-400 text-emerald-200 font-extrabold text-[11px] uppercase tracking-wider shadow-[0_0_25px_rgba(16,185,129,0.8)] hover:border-emerald-300 hover:scale-105 hover:text-white transition-all cursor-pointer whitespace-nowrap backdrop-blur-md active:scale-95"
              title="Open Portal Data tab to inspect, train, and manage Knowledge & Context Base full-screen"
            >
              <Database size={13} className="text-emerald-300 animate-pulse" />
              <span>View Data in Portal</span>
              <ArrowRight size={13} className="text-emerald-400" />
            </button>
          </div>
        )}

        {/* ── Directional Connection Handles ── */}
        <Handle
          type="target"
          position={Position.Left}
          id="in-knowledge"
          className="!bg-emerald-400 !w-3.5 !h-3.5 !border-2 !border-slate-950 !-left-2 transition-transform hover:scale-125 z-20 shadow-[0_0_8px_rgba(16,185,129,0.8)]"
          title="Input: Chained Knowledge Source"
        />
        <Handle
          type="source"
          position={Position.Right}
          id="out-knowledge"
          className="!bg-emerald-400 !w-3.5 !h-3.5 !border-2 !border-slate-950 !-right-2 transition-transform hover:scale-125 z-20 shadow-[0_0_8px_rgba(16,185,129,0.8)]"
          title="Output to Luna Core Hub"
        />
        <Handle
          type="target"
          position={Position.Top}
          id="in-top"
          className="!bg-emerald-400 !w-3.5 !h-3.5 !border-2 !border-slate-950 !-top-2 transition-transform hover:scale-125"
        />

        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-950/80 via-[#071313]/95 to-slate-900/80 px-4 py-3 border-b border-white/[0.08] flex items-center justify-between rounded-t-2xl shadow-[inset_0_1px_0_0_rgba(255,255,255,0.1)]">
          <div className="flex items-center gap-2.5">
            <div className="p-1.5 rounded-lg bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 shadow-[0_0_12px_rgba(16,185,129,0.3)]">
              <BookOpen size={16} />
            </div>
            <div>
              <h4 className="text-xs font-black text-slate-100 tracking-wide">Knowledge & Context</h4>
              <p className="text-[10px] text-emerald-300/70 font-mono">Autonomous RAG Vault</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            {saveIndicator && (
              <span className="text-[9px] font-mono font-bold text-emerald-400 animate-pulse bg-emerald-500/10 px-2 py-0.5 rounded-full border border-emerald-500/30">
                {saveIndicator}
              </span>
            )}
            <span className="text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
              TRAINING
            </span>
          </div>
        </div>

        {/* Wiring Live Status Bar */}
        <div className="mx-4 mt-3">
          {isWired ? (
            <div className="px-2.5 py-1 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-[10px] flex items-center justify-between font-mono">
              <span className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                <Zap size={11} className="text-emerald-400" /> Wired to Luna Core Hub
              </span>
              <span className="text-[9px] font-bold text-emerald-400 uppercase tracking-wide">ACTIVE</span>
            </div>
          ) : (
            <div className="px-2.5 py-1 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-300 text-[10px] flex items-center justify-between font-mono">
              <span className="flex items-center gap-1.5">
                <AlertCircle size={11} className="text-amber-400" /> Disconnected
              </span>
              <span className="text-[9px] text-amber-400/80">Connect to Luna Core Hub</span>
            </div>
          )}
        </div>

        {isCompactView ? (
          <div className="p-4 space-y-2.5 text-xs">
            <div className="p-3 rounded-2xl bg-slate-900/80 border border-emerald-500/25 space-y-2.5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <BookOpen size={14} className="text-emerald-400" />
                  <span className="font-bold text-white text-xs truncate max-w-[180px]">
                    {nodeData.lastTrainedFileName || (nodeData.knowledgeType === 'catalog' ? 'Product Catalog' : 'Autonomous Knowledge Base')}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => openInspector && openInspector(id)}
                  className="nodrag px-2.5 py-1 rounded-lg text-[10px] font-bold bg-emerald-500/20 hover:bg-emerald-500/35 text-emerald-200 border border-emerald-400/40 transition cursor-pointer active:scale-95 shadow-sm"
                >
                  Inspect ⚙️
                </button>
              </div>

              <div className="flex flex-wrap gap-1.5 pt-1">
                <span className="px-2 py-0.5 rounded-md bg-slate-800 border border-slate-700 text-[10px] font-mono text-emerald-300 font-bold">
                  {nodeData.knowledgeType?.toUpperCase() || 'DOCUMENT'}
                </span>
                {nodeData.lastTrainedAt && (
                  <span className="px-2 py-0.5 rounded-md bg-slate-800 border border-slate-700 text-[10px] font-mono text-slate-400">
                    Trained {new Date(nodeData.lastTrainedAt).toLocaleDateString()}
                  </span>
                )}
                <span className="px-2 py-0.5 rounded-md bg-emerald-950/70 border border-emerald-500/30 text-[10px] font-mono text-emerald-300">
                  RAG Active
                </span>
              </div>
            </div>
          </div>
        ) : (
          <div className="p-4 space-y-3.5 text-xs">
          {/* AI Training Engine Badge */}
          <div className="p-2 rounded-xl bg-emerald-950/40 border border-emerald-500/20 flex items-center justify-between text-[11px]">
            <div className="flex items-center gap-2 text-emerald-200 font-medium">
              <Brain size={14} className="text-emerald-400" />
              <span>AI Training Engine:</span>
            </div>
            <span className="font-mono text-[10px] font-bold text-cyan-300 bg-cyan-950/60 px-2 py-0.5 rounded border border-cyan-500/20">
              Training Engine
            </span>
          </div>

          {/* Source Type Selector */}
          <div>
            <label className="block text-[10px] font-black uppercase tracking-wider text-slate-400 mb-1.5 flex items-center gap-1.5">
              <Database size={12} className="text-emerald-400" /> Training Mode / Source
            </label>
            <select
              disabled={isTraining}
              className="nodrag w-full bg-slate-900/90 border border-white/10 rounded-xl px-3 py-2 text-slate-100 text-xs disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400/30 transition-all cursor-pointer font-medium"
              value={nodeData.knowledgeType || 'pdf'}
              onChange={(e) => handleChange('knowledgeType', e.target.value)}
            >
              <option value="pdf" className="bg-slate-900 text-slate-100">Multimodal Documents (PDF / Word / OCR)</option>
              <option value="catalog" className="bg-slate-900 text-slate-100">Live Inventory, Pricing & SKUs</option>
              <option value="faq" className="bg-slate-900 text-slate-100">Custom Q&A Training Matrix</option>
              <option value="custom" className="bg-slate-900 text-slate-100">Direct Markdown Knowledge Base</option>
            </select>
          </div>

          {/* ── Autonomous Document / Text Dropzone & Training Engine ── */}
          <div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.doc,.docx,.txt,.md,.csv,.json,image/*,audio/*,video/*"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleFileSelected(file);
                }}
              />

              {/* Training Tab Switcher */}
              <div className="flex items-center gap-1 p-1 mb-2 rounded-xl bg-slate-900/90 border border-white/10">
                <button
                  type="button"
                  disabled={isTraining}
                  onClick={() => setTrainingTab('upload')}
                  className={`nodrag flex-1 py-1.5 px-2 rounded-lg text-[10px] font-bold transition-all flex items-center justify-center gap-1.5 cursor-pointer ${
                    trainingTab === 'upload'
                      ? 'bg-emerald-500 text-slate-950 shadow-md'
                      : 'text-slate-400 hover:text-slate-200'
                  }`}
                >
                  <UploadCloud size={12} />
                  <span>Upload File</span>
                </button>
                <button
                  type="button"
                  disabled={isTraining}
                  onClick={() => setTrainingTab('text')}
                  className={`nodrag flex-1 py-1.5 px-2 rounded-lg text-[10px] font-bold transition-all flex items-center justify-center gap-1.5 cursor-pointer ${
                    trainingTab === 'text'
                      ? 'bg-emerald-500 text-slate-950 shadow-md'
                      : 'text-slate-400 hover:text-slate-200'
                  }`}
                >
                  <Type size={12} />
                  <span>Paste Text</span>
                </button>
                <button
                  type="button"
                  disabled={isTraining}
                  onClick={() => setTrainingTab('scrape')}
                  className={`nodrag flex-1 py-1.5 px-2 rounded-lg text-[10px] font-bold transition-all flex items-center justify-center gap-1.5 cursor-pointer ${
                    trainingTab === 'scrape'
                      ? 'bg-emerald-500 text-slate-950 shadow-md'
                      : 'text-slate-400 hover:text-slate-200'
                  }`}
                >
                  <Globe size={12} />
                  <span>Web Scrape</span>
                </button>
              </div>

              {isTraining ? (
                <div className="p-3.5 rounded-xl bg-emerald-950/40 border border-emerald-500/40 text-center space-y-2 relative overflow-hidden">
                  <div className="flex items-center justify-center gap-2 text-emerald-300 font-bold text-xs">
                    <Brain size={16} className="animate-pulse text-emerald-400" />
                    <span>
                      {trainingPhase === 'uploading'
                        ? 'Fetching & Crawling Target URL...'
                        : 'Synthesizing with Luna Neural Core...'}
                    </span>
                  </div>
                  <p className="text-[10px] text-slate-400 font-mono">
                    {trainingPhase === 'uploading'
                      ? 'Extracting clean web content & metadata...'
                      : 'Auto-organizing facts & resolving conflicts...'}
                  </p>
                  <div className="w-full bg-slate-900 rounded-full h-1.5 overflow-hidden">
                    <div className="bg-gradient-to-r from-emerald-500 via-teal-400 to-cyan-400 h-full w-full animate-pulse" />
                  </div>
                </div>
              ) : trainingTab === 'upload' ? (
                <div
                  onDragOver={(e) => {
                    e.preventDefault();
                    setIsDragging(true);
                  }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setIsDragging(false);
                    const file = e.dataTransfer.files?.[0];
                    if (file) handleFileSelected(file);
                  }}
                  onClick={() => fileInputRef.current?.click()}
                  className={`nodrag group cursor-pointer p-3 rounded-xl border border-dashed transition-all flex flex-col items-center justify-center gap-1.5 text-center ${
                    isDragging
                      ? 'border-emerald-400 bg-emerald-950/50 shadow-[0_0_20px_rgba(16,185,129,0.3)]'
                      : 'border-emerald-500/30 hover:border-emerald-400/70 bg-slate-900/60 hover:bg-slate-900/90'
                  }`}
                >
                  <div className="p-2 rounded-lg bg-emerald-500/10 text-emerald-400 group-hover:scale-110 transition-transform">
                    <UploadCloud size={16} />
                  </div>
                  <div>
                    <span className="text-[11px] font-bold text-slate-200 block">
                      {nodeData.contextSource ? 'Merge or Replace with Document' : 'Upload Document to Train AI'}
                    </span>
                    <span className="text-[9px] text-slate-500 font-mono">
                      PDF, DOCX, TXT, CSV, Images (Auto-Organized)
                    </span>
                  </div>
                </div>
              ) : trainingTab === 'text' ? (
                /* ── Raw Text Paste & Ingest Card ── */
                <div className="nodrag space-y-2 p-2.5 rounded-xl bg-slate-900/70 border border-emerald-500/30">
                  <input
                    type="text"
                    disabled={isTraining}
                    value={rawTextTitle}
                    onChange={(e) => setRawTextTitle(e.target.value)}
                    placeholder="Document Title (e.g. Course Syllabus, Policies)..."
                    className="nodrag w-full bg-slate-950/90 border border-white/10 rounded-lg px-2.5 py-1.5 text-slate-200 text-[11px] placeholder:text-slate-600 focus:outline-none focus:border-emerald-400"
                  />
                  <textarea
                    rows={4}
                    disabled={isTraining}
                    value={rawTextInput}
                    onChange={(e) => setRawTextInput(e.target.value)}
                    placeholder="Paste text here to train AI (e.g. course outlines, rules, FAQs, price lists in Sinhala or English)..."
                    className="nodrag nowheel w-full bg-slate-950/90 border border-white/10 rounded-lg p-2 text-slate-200 text-[11px] placeholder:text-slate-600 focus:outline-none focus:border-emerald-400 resize-none font-mono leading-relaxed"
                  />
                  <div className="flex items-center justify-between pt-1">
                    <span className="text-[9px] font-mono text-slate-500">
                      {rawTextInput.length} chars
                    </span>
                    <div className="flex items-center gap-1.5">
                      {rawTextInput.length > 0 && (
                        <button
                          type="button"
                          onClick={() => {
                            setRawTextInput('');
                            setRawTextTitle('');
                          }}
                          className="px-2 py-1 rounded text-[9px] font-mono text-slate-400 hover:text-slate-200"
                        >
                          Clear
                        </button>
                      )}
                      <button
                        type="button"
                        disabled={!rawTextInput.trim() || isTraining}
                        onClick={handleRawTextSubmit}
                        className="px-3 py-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 disabled:cursor-not-allowed text-slate-950 text-[10px] font-black tracking-wide flex items-center gap-1.5 transition-all shadow-md active:scale-95 cursor-pointer"
                      >
                        <Sparkles size={11} />
                        <span>Train with AI</span>
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                /* ── Web Scrape & URL Ingestion Card ── */
                <div className="nodrag space-y-2.5 p-3 rounded-xl bg-slate-900/80 border border-emerald-500/30">
                  <div>
                    <label className="text-[10px] font-bold text-slate-300 mb-1 flex items-center gap-1.5">
                      <Globe size={11} className="text-emerald-400" /> Target Website Domain / URL:
                    </label>
                    <input
                      type="url"
                      disabled={isTraining}
                      value={targetUrlInput}
                      onChange={(e) => setTargetUrlInput(e.target.value)}
                      placeholder="https://kangavena.com or example.com"
                      className="nodrag w-full bg-slate-950/90 border border-white/10 rounded-lg px-2.5 py-1.5 text-slate-200 text-[11px] placeholder:text-slate-600 focus:outline-none focus:border-emerald-400 font-mono"
                    />
                  </div>

                  <div className="flex items-center justify-between gap-2 pt-0.5">
                    <div className="flex items-center gap-1 bg-slate-950/80 p-0.5 rounded-lg border border-white/5">
                      <button
                        type="button"
                        disabled={isTraining}
                        onClick={() => setUrlCrawlDepth('single')}
                        className={`px-2 py-1 rounded text-[9px] font-bold transition cursor-pointer ${
                          urlCrawlDepth === 'single'
                            ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 shadow-sm'
                            : 'text-slate-500 hover:text-slate-300'
                        }`}
                      >
                        Single Page
                      </button>
                      <button
                        type="button"
                        disabled={isTraining}
                        onClick={() => setUrlCrawlDepth('deep')}
                        className={`px-2 py-1 rounded text-[9px] font-bold transition cursor-pointer ${
                          urlCrawlDepth === 'deep'
                            ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 shadow-sm'
                            : 'text-slate-500 hover:text-slate-300'
                        }`}
                      >
                        Domain Crawl
                      </button>
                    </div>

                    <button
                      type="button"
                      disabled={!targetUrlInput.trim() || isTraining}
                      onClick={handleUrlScrapeSubmit}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 disabled:cursor-not-allowed text-slate-950 font-black text-[10px] uppercase tracking-wider transition-all shadow-md active:scale-95 cursor-pointer"
                    >
                      <Sparkles size={11} />
                      <span>Scrape & Ingest</span>
                    </button>
                  </div>
                </div>
              )}

              {trainingError && (
                <div className="mt-2 p-2 rounded-lg bg-rose-500/10 border border-rose-500/30 flex items-start gap-1.5 text-[10px] text-rose-300">
                  <AlertCircle size={12} className="shrink-0 mt-0.5 text-rose-400" />
                  <span>{trainingError}</span>
                </div>
              )}
            </div>

          {/* ── Document Digest Status Badge ── */}
          {nodeData.lastTrainedFileName && (
            <div className="p-2.5 rounded-xl bg-slate-900/80 border border-emerald-500/20 space-y-1.5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5 text-[10px] font-bold text-emerald-300 truncate max-w-[180px]">
                  <FileUp size={12} className="shrink-0 text-emerald-400" />
                  <span className="truncate">{nodeData.lastTrainedFileName}</span>
                </div>
                <span className="text-[9px] font-mono text-slate-400">
                  {nodeData.lastTrainedFileSize
                    ? `${(nodeData.lastTrainedFileSize / 1024).toFixed(1)} KB`
                    : 'Ingested'}
                </span>
              </div>

              {nodeData.autoKeywords && (
                <div className="flex items-center gap-1 text-[9px] text-teal-300/80 font-mono truncate">
                  <Tag size={10} className="shrink-0 text-teal-400" />
                  <span className="truncate">{nodeData.autoKeywords}</span>
                </div>
              )}
            </div>
          )}

          {/* ── Knowledge Base Content (Interactive Executive Capsule & Full Explorer) ── */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-[10px] font-black uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
                <FileText size={12} className="text-emerald-400" /> Synthesized Knowledge Base
              </label>
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => setIsEditMode(!isEditMode)}
                  className="nodrag text-[9px] text-slate-400 hover:text-emerald-300 transition-colors flex items-center gap-1 font-mono cursor-pointer"
                >
                  {isEditMode ? <Eye size={10} /> : <Edit3 size={10} />}
                  {isEditMode ? 'Capsule' : 'Inline'}
                </button>
                {charCount > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      if (confirm('Clear all knowledge from this node?')) {
                        handleBulkUpdate({
                          contextSource: '',
                          lastTrainedFileName: undefined,
                          lastTrainedFileSize: undefined,
                          autoKeywords: undefined,
                        });
                      }
                    }}
                    className="nodrag text-[9px] text-rose-400/70 hover:text-rose-300 transition-colors cursor-pointer"
                    title="Clear knowledge"
                  >
                    <Trash2 size={10} />
                  </button>
                )}
              </div>
            </div>

            {isEditMode ? (
              <textarea
                ref={contextTextareaRef}
                rows={5}
                disabled={isTraining}
                className="nodrag nowheel w-full bg-slate-900/90 border border-white/10 rounded-xl px-3 py-2 text-slate-100 text-xs disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400/30 transition-all placeholder:text-slate-600 resize-none font-mono text-[11px] leading-relaxed"
                placeholder="Upload a document or paste text above to train Luna AI..."
                value={localContextSource}
                onChange={handleContextSourceChange}
                onFocus={handleContextSourceFocus}
                onBlur={handleContextSourceBlur}
              />
            ) : (
              /* ── Interactive Categorized Knowledge Capsule ── */
              <div
                onClick={() => setIsModalOpen(true)}
                className="nodrag nowheel group cursor-pointer w-full bg-slate-900/80 hover:bg-slate-900 border border-emerald-500/20 hover:border-emerald-400/50 rounded-xl p-3 text-slate-300 transition-all space-y-2.5 shadow-lg"
              >
                {nodeData.contextSource ? (
                  <>
                    {/* Category Badges Row */}
                    {detectedCategories.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {detectedCategories.map((cat, idx) => {
                          const IconComp = cat.icon;
                          return (
                            <span
                              key={idx}
                              className={`text-[9px] font-bold px-2 py-0.5 rounded-md border flex items-center gap-1 font-mono ${cat.color}`}
                            >
                              <IconComp size={10} />
                              <span>{cat.label}</span>
                            </span>
                          );
                        })}
                      </div>
                    )}

                    {/* Sneak-Peek Snippet with Gradient Fade */}
                    <div className="relative max-h-16 overflow-hidden">
                      <p className="font-mono text-[10px] text-slate-300 leading-snug line-clamp-3">
                        {nodeData.contextSource.slice(0, 180)}...
                      </p>
                      <div className="absolute inset-x-0 bottom-0 h-6 bg-gradient-to-t from-slate-900 to-transparent pointer-events-none" />
                    </div>

                    {/* Interactive Full-Screen Explorer Trigger Button */}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsModalOpen(true);
                      }}
                      className="w-full py-1.5 px-3 rounded-lg bg-emerald-500/10 group-hover:bg-emerald-500/20 border border-emerald-500/30 group-hover:border-emerald-400/60 text-emerald-300 text-[10px] font-black flex items-center justify-between transition-all cursor-pointer"
                    >
                      <span className="flex items-center gap-1.5">
                        <Maximize2 size={12} className="text-emerald-400" />
                        <span>Explore & Edit Full Knowledge</span>
                      </span>
                      <ChevronRight size={13} className="group-hover:translate-x-0.5 transition-transform" />
                    </button>
                  </>
                ) : (
                  <div className="py-2 text-center text-slate-600 italic text-[10px]">
                    No knowledge ingested yet. Drop a document or paste text above to begin training.
                  </div>
                )}
              </div>
            )}

            {charCount > 0 && (
              <div className="flex items-center justify-between mt-1 text-[9px] text-slate-500 font-mono">
                <span>{wordCount} words • {charCount} chars</span>
                <span className="text-emerald-400 flex items-center gap-1 font-bold">
                  <Sparkles size={9} /> Luna RAG Ready
                </span>
              </div>
            )}
          </div>

          {/* ── Auto-Sync & Telemetry ── */}
          <div className="flex items-center justify-between pt-1 border-t border-white/5">
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={nodeData.autoSync ?? true}
                onChange={(e) => handleChange('autoSync', e.target.checked)}
                className="nodrag rounded border-white/20 bg-slate-900 text-emerald-500 focus:ring-0 focus:ring-offset-0 cursor-pointer"
              />
              <span className="text-[10px] text-slate-300 font-medium">Live Auto-Sync</span>
            </label>
            <span className="text-[9px] text-emerald-400 flex items-center gap-1 font-mono">
              <CheckCircle2 size={10} /> Active RAG
            </span>
          </div>
        </div>
        )}

        <Handle
          type="source"
          position={Position.Bottom}
          id="out-bottom"
          className="!bg-emerald-400 !w-3.5 !h-3.5 !border-2 !border-slate-950 !-bottom-2 transition-transform hover:scale-125"
        />
      </div>

      {/* ── Ingestion Strategy Choice Modal (Smart Merge vs Replace) ── */}
      {showIngestModal && (pendingFile || pendingRawText) && createPortal(
        <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/85 backdrop-blur-md p-4 animate-in fade-in duration-200 font-sans">
          <div className="relative w-full max-w-lg bg-slate-950 border border-emerald-500/40 rounded-3xl p-6 shadow-[0_0_60px_rgba(16,185,129,0.3)] text-slate-100 space-y-5">
            
            {/* Modal Header */}
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-2xl bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 shadow-[0_0_15px_rgba(16,185,129,0.4)]">
                  <Brain size={22} className="animate-pulse" />
                </div>
                <div>
                  <h3 className="text-base font-black text-slate-100 flex items-center gap-2">
                    <span>Knowledge Ingestion Strategy</span>
                  </h3>
                  <p className="text-xs text-slate-400 font-mono">
                    Choose how Luna AI should process the new content
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowIngestModal(false);
                  setPendingFile(null);
                  setPendingRawText(null);
                }}
                className="text-slate-400 hover:text-slate-200 p-1 rounded-lg hover:bg-white/5 transition"
              >
                <X size={16} />
              </button>
            </div>

            {/* Selected File / Text Badge */}
            <div className="p-3 rounded-2xl bg-slate-900/90 border border-white/10 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2.5 min-w-0">
                {pendingFile ? (
                  <FileText size={16} className="text-emerald-400 shrink-0" />
                ) : (
                  <Type size={16} className="text-emerald-400 shrink-0" />
                )}
                <div className="min-w-0">
                  <p className="text-xs font-bold text-slate-200 truncate">
                    {pendingFile ? pendingFile.name : (pendingRawText?.title || 'Pasted Text Notes')}
                  </p>
                  <p className="text-[10px] text-slate-500 font-mono">
                    {pendingFile
                      ? `${(pendingFile.size / 1024).toFixed(1)} KB • Multimodal Document`
                      : `${pendingRawText?.text.length || 0} characters • Raw Text Ingestion`}
                  </p>
                </div>
              </div>
              <span className="text-[9px] font-mono px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-300 border border-emerald-500/30 uppercase tracking-wider shrink-0">
                Ready to ingest
              </span>
            </div>

            {/* Strategy Choices */}
            <div className="space-y-3">
              {/* Option 1: Smart Merge (Recommended) */}
              <div
                onClick={() => setSelectedIngestMode('merge')}
                className={`p-4 rounded-2xl border transition-all cursor-pointer space-y-1.5 ${
                  selectedIngestMode === 'merge'
                    ? 'bg-emerald-950/40 border-emerald-400 ring-2 ring-emerald-400/30 shadow-[0_0_25px_rgba(16,185,129,0.25)]'
                    : 'bg-slate-900/60 border-white/10 hover:border-emerald-500/40 hover:bg-slate-900/90'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <RefreshCw size={15} className={selectedIngestMode === 'merge' ? 'text-emerald-300' : 'text-slate-400'} />
                    <span className="text-xs font-black text-slate-100">
                      🔄 Smart Update & Merge
                    </span>
                  </div>
                  <span className="text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">
                    Recommended
                  </span>
                </div>
                <p className="text-[11px] text-slate-300 leading-relaxed font-sans pl-6">
                  Luna AI will ingest the new content and <strong className="text-emerald-300">intelligently merge</strong> with existing knowledge. Changed facts (e.g. new pricing, hours) are updated while preserving all other existing non-conflicting sections.
                </p>
              </div>

              {/* Option 2: Clean Replace & Reset */}
              <div
                onClick={() => setSelectedIngestMode('replace')}
                className={`p-4 rounded-2xl border transition-all cursor-pointer space-y-1.5 ${
                  selectedIngestMode === 'replace'
                    ? 'bg-rose-950/40 border-rose-400 ring-2 ring-rose-400/30 shadow-[0_0_25px_rgba(244,63,94,0.25)]'
                    : 'bg-slate-900/60 border-white/10 hover:border-rose-500/40 hover:bg-slate-900/90'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Trash2 size={15} className={selectedIngestMode === 'replace' ? 'text-rose-300' : 'text-slate-400'} />
                    <span className="text-xs font-black text-slate-100">
                      ⚠️ Clean Replace & Reset
                    </span>
                  </div>
                  <span className="text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full bg-rose-500/20 text-rose-300 border border-rose-500/40">
                    Clean Slate
                  </span>
                </div>
                <p className="text-[11px] text-slate-300 leading-relaxed font-sans pl-6">
                  <strong className="text-rose-300">Erases all previous knowledge</strong> in this node and builds a fresh knowledge base containing ONLY information from this new input.
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => {
                  setShowIngestModal(false);
                  setPendingFile(null);
                  setPendingRawText(null);
                  setPendingUrlScrape(null);
                }}
                className="px-4 py-2 rounded-xl text-xs font-bold text-slate-400 hover:text-slate-200 transition cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  if (pendingFile) {
                    executeFileUpload(pendingFile, selectedIngestMode);
                  } else if (pendingRawText) {
                    executeRawTextTraining(pendingRawText.text, pendingRawText.title, selectedIngestMode);
                  } else if (pendingUrlScrape) {
                    executeUrlScrapeTraining(pendingUrlScrape.url, pendingUrlScrape.depth, selectedIngestMode);
                  }
                }}
                className={`px-5 py-2.5 rounded-xl text-xs font-black transition-all shadow-lg flex items-center gap-2 cursor-pointer ${
                  selectedIngestMode === 'replace'
                    ? 'bg-rose-500 hover:bg-rose-400 text-slate-950 shadow-[0_0_20px_rgba(244,63,94,0.4)]'
                    : 'bg-emerald-500 hover:bg-emerald-400 text-slate-950 shadow-[0_0_20px_rgba(16,185,129,0.4)]'
                }`}
              >
                <Sparkles size={14} />
                <span>
                  {selectedIngestMode === 'replace'
                    ? 'Replace Knowledge & Train'
                    : 'Merge & Train Knowledge'}
                </span>
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ── Full-Screen Interactive Knowledge Explorer & Editor Modal ── */}
      <KnowledgeExplorerModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        knowledgeText={nodeData.contextSource || ''}
        onSave={(updated) => {
          handleChange('contextSource', updated);
        }}
        fileName={nodeData.lastTrainedFileName}
        fileSize={nodeData.lastTrainedFileSize}
        lastTrainedAt={nodeData.lastTrainedAt}
        autoKeywords={nodeData.autoKeywords}
      />
    </>
  );
});

KnowledgeNode.displayName = 'KnowledgeNode';
export default KnowledgeNode;

